import type { TriggerRuleConfig } from './types';

const DEFAULT_ESTIMATED_MINUTES_PER_QUESTION = 2;

/**
 * Question keys (externalId or _id) used for trigger context.
 * Gender: "Sex assigned at birth" in New Client Health Profile.
 * Age: optional; when provided, age-based rules (e.g. ChildTeen) can match.
 * Pregnancy: keys used by questionnaire variants for pregnancy status.
 */
const CONTEXT = {
  gender: [
    '696f888e02616daa6cbaea47',
    'gender',
    'sex',
    'biologicalSex',
    'biological_sex',
    'sexAtBirth',
    'sex_at_birth',
    'sexAssignedAtBirth',
    'sex_assigned_at_birth',
  ],
  age: [
    'age',
    'Age',
    'clientAge',
    'ageYears',
    'birthYear',
    'yearOfBirth',
    'year_of_birth',
    'dob',
    'dateOfBirth',
    'date_of_birth',
  ],
  pregnancy: [
    'pregnancyStatus',
    'pregnancy_status',
    'pregnancyTrimester',
    'pregnancy_trimester',
    'isPregnant',
    'pregnant',
  ],
} as const;

/**
 * Questionnaire IDs from GET /questionnaires (use _id).
 */
const Q = {
  DM_Gestational: '696f888e02616daa6cbae9ac',
  DM_LADA_Men: '696f888e02616daa6cbae9b7',
  DM_LADA_Women: '696f888e02616daa6cbae9c4',
  DM_T1_ChildTeen: '696f888e02616daa6cbae9d3',
  DM_T1_Men: '696f888e02616daa6cbae9e3',
  DM_T1_Women: '696f888e02616daa6cbae9f0',
  DM_T2_ChildTeen: '696f888e02616daa6cbae9ff',
  DM_T2_Men: '696f888e02616daa6cbaea0d',
  DM_T2_Women: '696f888e02616daa6cbaea17',
  IR_ChildTeen: '696f888e02616daa6cbaea24',
  IR_Men: '696f888e02616daa6cbaea2f',
  IR_Women: '696f888e02616daa6cbaea38',
  PCOS_Adolescent: '696f888e02616daa6cbaea65',
  PCOS_Women: '696f888e02616daa6cbaea74',
  Thyroid_Hashimotos_ChildTeen: '696f888e02616daa6cbaea8e',
  Thyroid_Hashimotos_Men: '696f888e02616daa6cbaea99',
  Thyroid_Hashimotos_Women: '89fbb63bc93bc917b673f6ca',
  Thyroid_Hyper_ChildTeen: '696f888e02616daa6cbaeaad',
  Thyroid_Hyper_Men: '696f888e02616daa6cbaeab7',
  Thyroid_Hyper_Women: '696f888e02616daa6cbaeabf',
  Thyroid_Hypo_ChildTeen: '696f888e02616daa6cbaeac9',
  Thyroid_Hypo_Men: '696f888e02616daa6cbaead4',
  Thyroid_Hypo_Women: '696f888e02616daa6cbaeadd',
} as const;

/**
 * Default trigger rules: map selectable values to questionnaires.
 * triggerValues = option "value" from API (e.g. "Have you been diagnosed...", "If Diabetes: which type?", "If Thyroid: which type?").
 * Context: gender from "Sex assigned at birth", age when provided, pregnancy status where available.
 */
export const defaultTriggerRules: TriggerRuleConfig = {
  estimatedTimePerQuestionMinutes: DEFAULT_ESTIMATED_MINUTES_PER_QUESTION,
  contextFields: {
    gender: CONTEXT.gender,
    age: CONTEXT.age,
    pregnancy: CONTEXT.pregnancy,
  },
  rules: [
    /* ----- Diabetes type ("If Diabetes: which type?") ----- */
    {
      id: 'r-dm-gestational',
      questionnaireId: Q.DM_Gestational,
      triggerValues: ['Gestational'],
      conditions: { gender: 'female', ageMin: 18, pregnant: true },
      triggerReason: 'Selected: Diabetes type Gestational (female, age >=18, pregnant)',
      order: 1,
    },
    {
      id: 'r-dm-t1-men',
      questionnaireId: Q.DM_T1_Men,
      triggerValues: ['Type 1'],
      conditions: { gender: 'male', ageMin: 18 },
      triggerReason: 'Selected: Type 1 diabetes (male)',
      order: 10,
    },
    {
      id: 'r-dm-t1-women',
      questionnaireId: Q.DM_T1_Women,
      triggerValues: ['Type 1'],
      conditions: { gender: 'female', ageMin: 18 },
      triggerReason: 'Selected: Type 1 diabetes (female)',
      order: 10,
    },
    {
      id: 'r-dm-t1-childteen',
      questionnaireId: Q.DM_T1_ChildTeen,
      triggerValues: ['Type 1'],
      conditions: { ageMax: 17 },
      triggerReason: 'Selected: Type 1 diabetes (age <18)',
      order: 5,
    },
    {
      id: 'r-dm-t2-men',
      questionnaireId: Q.DM_T2_Men,
      triggerValues: ['Type 2'],
      conditions: { gender: 'male', ageMin: 18 },
      triggerReason: 'Selected: Type 2 diabetes (male)',
      order: 10,
    },
    {
      id: 'r-dm-t2-women',
      questionnaireId: Q.DM_T2_Women,
      triggerValues: ['Type 2'],
      conditions: { gender: 'female', ageMin: 18 },
      triggerReason: 'Selected: Type 2 diabetes (female)',
      order: 10,
    },
    {
      id: 'r-dm-t2-childteen',
      questionnaireId: Q.DM_T2_ChildTeen,
      triggerValues: ['Type 2'],
      conditions: { ageMax: 17 },
      triggerReason: 'Selected: Type 2 diabetes (age <18)',
      order: 5,
    },
    {
      id: 'r-dm-lada-men',
      questionnaireId: Q.DM_LADA_Men,
      triggerValues: ['LADA'],
      conditions: { gender: 'male', ageMin: 18 },
      triggerReason: 'Selected: LADA (male)',
      order: 10,
    },
    {
      id: 'r-dm-lada-women',
      questionnaireId: Q.DM_LADA_Women,
      triggerValues: ['LADA'],
      conditions: { gender: 'female', ageMin: 18 },
      triggerReason: 'Selected: LADA (female)',
      order: 10,
    },
    /* ----- Insulin resistance ("Have you been diagnosed..." / "Do you have any diagnosed...") ----- */
    {
      id: 'r-ir-men',
      questionnaireId: Q.IR_Men,
      triggerValues: ['Insulin resistance'],
      conditions: { gender: 'male', ageMin: 18 },
      triggerReason: 'Selected: Insulin resistance (male)',
      order: 10,
    },
    {
      id: 'r-ir-women',
      questionnaireId: Q.IR_Women,
      triggerValues: ['Insulin resistance'],
      conditions: { gender: 'female', ageMin: 18 },
      triggerReason: 'Selected: Insulin resistance (female)',
      order: 10,
    },
    {
      id: 'r-ir-childteen',
      questionnaireId: Q.IR_ChildTeen,
      triggerValues: ['Insulin resistance'],
      conditions: { ageMax: 17 },
      triggerReason: 'Selected: Insulin resistance (age <18)',
      order: 5,
    },
    /* ----- PCOS ----- */
    {
      id: 'r-pcos-adolescent',
      questionnaireId: Q.PCOS_Adolescent,
      triggerValues: ['PCOS'],
      conditions: { gender: 'female', ageMax: 17 },
      triggerReason: 'Selected: PCOS (female, age <18)',
      order: 5,
    },
    {
      id: 'r-pcos-women',
      questionnaireId: Q.PCOS_Women,
      triggerValues: ['PCOS'],
      conditions: { gender: 'female', ageMin: 18 },
      triggerReason: 'Selected: PCOS (female)',
      order: 10,
    },
    /* ----- Thyroid ("If Thyroid: which type?") ----- */
    {
      id: 'r-thyroid-hashimotos-men',
      questionnaireId: Q.Thyroid_Hashimotos_Men,
      triggerValues: ["Hashimoto's"],
      conditions: { gender: 'male', ageMin: 18 },
      triggerReason: "Selected: Hashimoto's (male)",
      order: 10,
    },
    {
      id: 'r-thyroid-hashimotos-women',
      questionnaireId: Q.Thyroid_Hashimotos_Women,
      triggerValues: ["Hashimoto's"],
      conditions: { gender: 'female', ageMin: 18 },
      triggerReason: "Selected: Hashimoto's (female)",
      order: 10,
    },
    {
      id: 'r-thyroid-hashimotos-childteen',
      questionnaireId: Q.Thyroid_Hashimotos_ChildTeen,
      triggerValues: ["Hashimoto's"],
      conditions: { ageMax: 17 },
      triggerReason: "Selected: Hashimoto's (age <18)",
      order: 5,
    },
    {
      id: 'r-thyroid-hyper-men',
      questionnaireId: Q.Thyroid_Hyper_Men,
      // Graves' is captured as a thyroid subtype but has no questionnaire yet.
      triggerValues: ['Overactive (hyperthyroid)'],
      conditions: { gender: 'male', ageMin: 18 },
      triggerReason: 'Selected: Hyperthyroid (male)',
      order: 10,
    },
    {
      id: 'r-thyroid-hyper-women',
      questionnaireId: Q.Thyroid_Hyper_Women,
      triggerValues: ['Overactive (hyperthyroid)'],
      conditions: { gender: 'female', ageMin: 18 },
      triggerReason: 'Selected: Hyperthyroid (female)',
      order: 10,
    },
    {
      id: 'r-thyroid-hyper-childteen',
      questionnaireId: Q.Thyroid_Hyper_ChildTeen,
      triggerValues: ['Overactive (hyperthyroid)'],
      conditions: { ageMax: 17 },
      triggerReason: 'Selected: Hyperthyroid (age <18)',
      order: 5,
    },
    {
      id: 'r-thyroid-hypo-men',
      questionnaireId: Q.Thyroid_Hypo_Men,
      triggerValues: ['Underactive (hypothyroid)'],
      conditions: { gender: 'male', ageMin: 18 },
      triggerReason: 'Selected: Hypothyroid (male)',
      order: 10,
    },
    {
      id: 'r-thyroid-hypo-women',
      questionnaireId: Q.Thyroid_Hypo_Women,
      triggerValues: ['Underactive (hypothyroid)'],
      conditions: { gender: 'female', ageMin: 18 },
      triggerReason: 'Selected: Hypothyroid (female)',
      order: 10,
    },
    {
      id: 'r-thyroid-hypo-childteen',
      questionnaireId: Q.Thyroid_Hypo_ChildTeen,
      triggerValues: ['Underactive (hypothyroid)'],
      conditions: { ageMax: 17 },
      triggerReason: 'Selected: Hypothyroid (age <18)',
      order: 5,
    },
  ],
};
